import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Space, Empty, Typography, Button, message } from "antd";
import {
  CameraOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Mapa simple para el detalle de finca. Pinta las parcelas declaradas
 * por contrato (azul) y las canónicas (verde) en capas separables, con
 * popup informativo en cada polígono y resaltado persistente al click.
 * Base ArcGIS Satellite igual que en el detalle de auditoría.
 *
 * Comportamiento del resaltado:
 *   - Click en un polígono → estilo "highlight" (borde grueso + relleno
 *     más opaco) + popup abierto.
 *   - Cerrar el popup NO quita el resaltado.
 *   - Click en el fondo del mapa o en otro polígono → resetea.
 */

interface PlotForMap {
  id: number;
  name: string | null;
  surface: number | null;
  surface_gps: number | null;
  geom_geojson: GeoJSON.Geometry | null;
  variety_name: string | null;
  // Datos extra que sólo aplican a parcelas canónicas — opcionales para
  // no obligar al caller a pasarlos en las declaradas.
  plants?: number | null;
  crop_date?: number | null;
  plantation_date?: string | null;
  plot_type_name?: string | null;
  rootstock_name?: string | null;
  plantation_frame?: string | null;
  season_name?: string | null;
  is_active?: boolean;
  status?: "open" | "closed";
  closed_date?: string | null;
}

interface Props {
  declaredPlots: PlotForMap[];
  canonicalPlots: PlotForMap[];
  height?: number;
  // Metadatos opcionales que se imprimen en la cabecera del PNG
  // exportado para que sea un documento autocontenido.
  farmName?: string | null;
  producerName?: string | null;
  locationLine?: string | null;
}

type LayerKey = "declared" | "canonical";

// Colores muy distinguibles entre sí, alineados con la paleta del
// detalle de auditoría: declaradas en teal (geometría de referencia),
// canónicas en naranja (geometría auditada). Ambos saturados y con
// suficiente contraste para impresión.
const COLOR_DECLARED = "#08979c"; // teal
const COLOR_CANONICAL = "#fa8c16"; // naranja

const baseStyle = (kind: LayerKey): L.PathOptions => ({
  color: kind === "declared" ? COLOR_DECLARED : COLOR_CANONICAL,
  weight: 2,
  fillColor: kind === "declared" ? COLOR_DECLARED : COLOR_CANONICAL,
  fillOpacity: kind === "declared" ? 0.2 : 0.3,
  dashArray: kind === "declared" ? "5 4" : undefined,
});

const highlightStyle = (kind: LayerKey): L.PathOptions => ({
  color: kind === "declared" ? "#00474f" : "#ad4e00",
  weight: 4,
  fillColor: kind === "declared" ? COLOR_DECLARED : COLOR_CANONICAL,
  fillOpacity: 0.55,
  dashArray: undefined,
});

const fmtNum = (v: number | null | undefined, dec = 2) =>
  v != null
    ? Number(v).toLocaleString("es-ES", {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      })
    : "—";

const buildPopup = (p: PlotForMap, kind: LayerKey) => `
  <div style="min-width:180px;font-size:12px;">
    <div style="font-weight:600;margin-bottom:4px;">
      ${kind === "declared" ? "Declarada" : "Canónica"}: ${p.name ?? `#${p.id}`}
    </div>
    ${p.variety_name ? `<div>Variedad: <strong>${p.variety_name}</strong></div>` : ""}
    ${p.surface != null ? `<div>Sup. ${kind === "declared" ? "licenciada" : "auditada"}: ${fmtNum(p.surface)} ha</div>` : ""}
    ${p.surface_gps != null ? `<div>Sup. GPS: ${fmtNum(p.surface_gps)} ha</div>` : ""}
  </div>
`;

/* ------------------------------------------------------------------ */
/* Legend                                                              */
/* ------------------------------------------------------------------ */

const Swatch: React.FC<{ kind: LayerKey }> = ({ kind }) => (
  <span
    style={{
      display: "inline-block",
      width: 14,
      height: 14,
      borderRadius: 3,
      background:
        kind === "declared"
          ? "rgba(8, 151, 156, 0.22)"
          : "rgba(250, 140, 22, 0.3)",
      border:
        kind === "declared"
          ? `2px dashed ${COLOR_DECLARED}`
          : `2px solid ${COLOR_CANONICAL}`,
    }}
  />
);

const LegendChip: React.FC<{
  label: string;
  kind: LayerKey;
  count: number;
  on: boolean;
  onClick: () => void;
}> = ({ label, kind, count, on, onClick }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      border: "1px solid",
      borderColor: on ? "#d9d9d9" : "#f0f0f0",
      borderRadius: 14,
      background: on ? "#fafafa" : "transparent",
      cursor: "pointer",
      userSelect: "none",
      fontSize: 12,
      color: on ? "#262626" : "#bfbfbf",
      textDecoration: on ? "none" : "line-through",
      opacity: on ? 1 : 0.6,
      transition: "opacity 0.15s, background 0.15s",
    }}
    title={`${label} — ${on ? "ocultar" : "mostrar"}`}
  >
    <Swatch kind={kind} />
    <span>{label}</span>
    <span
      style={{
        background: on ? "#fff" : "#fafafa",
        border: "1px solid",
        borderColor: on ? "#d9d9d9" : "#e8e8e8",
        color: on ? "#595959" : "#bfbfbf",
        padding: "0 6px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        minWidth: 18,
        textAlign: "center",
      }}
    >
      {count}
    </span>
  </span>
);

/* ------------------------------------------------------------------ */
/* Map                                                                 */
/* ------------------------------------------------------------------ */

export const FarmMapCard = ({
  declaredPlots,
  canonicalPlots,
  height = 520,
  farmName,
  producerName,
  locationLine,
}: Props) => {
  const [exporting, setExporting] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const declaredLayer = useRef<L.LayerGroup | null>(null);
  const canonicalLayer = useRef<L.LayerGroup | null>(null);

  // Map id → { layer, kind } so we can clear the highlight from any
  // previously selected polygon without scanning the whole tree.
  const polysRef = useRef<
    Map<string, { layer: L.GeoJSON; kind: LayerKey }>
  >(new Map());
  const highlightedRef = useRef<string | null>(null);

  const [visible, setVisible] = useState<Record<LayerKey, boolean>>({
    declared: true,
    canonical: true,
  });

  const declaredWithGeom = useMemo(
    () => declaredPlots.filter((p) => p.geom_geojson),
    [declaredPlots],
  );
  const canonicalWithGeom = useMemo(
    () => canonicalPlots.filter((p) => p.geom_geojson),
    [canonicalPlots],
  );
  const hasAnyGeom = declaredWithGeom.length + canonicalWithGeom.length > 0;

  const clearHighlight = () => {
    const cur = highlightedRef.current;
    if (!cur) return;
    const entry = polysRef.current.get(cur);
    if (entry) entry.layer.setStyle(baseStyle(entry.kind));
    highlightedRef.current = null;
  };

  // Init the map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      center: [40.02, -4.9],
      zoom: 14,
      zoomControl: true,
    });
    mapInstance.current = map;

    const arcgisSatellite = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "&copy; Esri" },
    );
    const arcgisLabels = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "&copy; Esri" },
    );
    const googleSatellite = L.tileLayer(
      "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      { maxZoom: 20, attribution: "&copy; Google" },
    );
    const osm = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { maxZoom: 19, attribution: "&copy; OpenStreetMap" },
    );

    arcgisSatellite.addTo(map);

    L.control
      .layers(
        {
          "Satélite (ArcGIS)": arcgisSatellite,
          "Satélite + etiquetas": L.layerGroup([arcgisSatellite, arcgisLabels]),
          "Satélite (Google)": googleSatellite,
          OpenStreetMap: osm,
        },
        undefined,
        { position: "topright", collapsed: true },
      )
      .addTo(map);

    declaredLayer.current = L.layerGroup().addTo(map);
    canonicalLayer.current = L.layerGroup().addTo(map);

    // Clicking on the map background (i.e. NOT a polygon — those stop
    // propagation in their own click handler) resets the highlight.
    map.on("click", () => clearHighlight());

    return () => {
      map.remove();
      mapInstance.current = null;
      declaredLayer.current = null;
      canonicalLayer.current = null;
      polysRef.current.clear();
      highlightedRef.current = null;
    };
  }, []);

  // (Re)paint polygons whenever the data changes
  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;

    declaredLayer.current?.clearLayers();
    canonicalLayer.current?.clearLayers();
    polysRef.current.clear();
    highlightedRef.current = null;

    const allLayers: L.Layer[] = [];

    const addPoly = (p: PlotForMap, kind: LayerKey) => {
      const layer = L.geoJSON(p.geom_geojson as any, {
        style: baseStyle(kind),
      });
      layer.bindPopup(buildPopup(p, kind));
      const key = `${kind}:${p.id}`;
      polysRef.current.set(key, { layer, kind });
      layer.on("click", (e) => {
        // Prevent the map-click handler from immediately clearing the
        // highlight we are about to set.
        L.DomEvent.stopPropagation(e as any);
        clearHighlight();
        layer.setStyle(highlightStyle(kind));
        highlightedRef.current = key;
        layer.openPopup();
      });
      const targetGroup =
        kind === "declared" ? declaredLayer.current : canonicalLayer.current;
      targetGroup?.addLayer(layer);
      allLayers.push(layer);
    };

    declaredWithGeom.forEach((p) => addPoly(p, "declared"));
    canonicalWithGeom.forEach((p) => addPoly(p, "canonical"));

    if (allLayers.length > 0) {
      const fg = L.featureGroup(allLayers);
      const bounds = fg.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [24, 24], maxZoom: 18 });
      }
    }
  }, [declaredWithGeom, canonicalWithGeom]);

  // Toggle layer visibility from the legend
  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;
    if (!declaredLayer.current || !canonicalLayer.current) return;

    const apply = (
      group: L.LayerGroup,
      on: boolean,
    ) => {
      if (on && !map.hasLayer(group)) group.addTo(map);
      if (!on && map.hasLayer(group)) map.removeLayer(group);
    };
    apply(declaredLayer.current, visible.declared);
    apply(canonicalLayer.current, visible.canonical);

    // Si el polígono resaltado pertenece a una capa que se acaba de
    // ocultar, limpiamos el resaltado para no dejar estado huérfano.
    const cur = highlightedRef.current;
    if (cur) {
      const [kind] = cur.split(":") as [LayerKey];
      if (!visible[kind]) clearHighlight();
    }
  }, [visible]);

  // Cuando el contenedor cambia de tamaño (layout responsive), Leaflet
  // necesita un invalidateSize() para no quedarse con dimensiones
  // viejas. Lo disparamos en cada render relevante.
  useEffect(() => {
    if (!mapInstance.current) return;
    const t = setTimeout(() => mapInstance.current?.invalidateSize(), 100);
    return () => clearTimeout(t);
  }, [height, hasAnyGeom]);

  /* ---- Export KML ----------------------------------------------- */

  // KML colors are AABBGGRR (alpha, blue, green, red) — invertido
  // respecto al hex web. Calculados a partir de COLOR_DECLARED y
  // COLOR_CANONICAL para que el documento se vea como el mapa.
  const KML_STYLE_DECLARED = {
    line: "ff9c9708", // teal #08979c sin alpha
    fill: "808c9708", // teal con 50% opacidad
  };
  const KML_STYLE_CANONICAL = {
    line: "ff168cfa", // naranja #fa8c16
    fill: "80168cfa",
  };

  const xmlEscape = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");

  // Serializa un ring (array de [lon, lat]) como
  // "lon,lat,0 lon,lat,0 ...". KML asume altitud 0 cuando no se da.
  const ringToCoords = (ring: number[][]): string =>
    ring.map((pt) => `${pt[0]},${pt[1]},0`).join(" ");

  // Construye el bloque <Polygon> o <MultiGeometry> según el tipo de
  // geometría GeoJSON. Sólo soportamos Polygon y MultiPolygon — los
  // únicos tipos que la finca genera en este contexto.
  const geometryToKml = (geom: GeoJSON.Geometry): string => {
    const polygonXml = (rings: number[][][]): string => {
      const outer = rings[0] || [];
      const holes = rings.slice(1);
      const holesXml = holes
        .map(
          (h) =>
            `<innerBoundaryIs><LinearRing><coordinates>${ringToCoords(h)}</coordinates></LinearRing></innerBoundaryIs>`,
        )
        .join("");
      return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${ringToCoords(outer)}</coordinates></LinearRing></outerBoundaryIs>${holesXml}</Polygon>`;
    };
    if (geom.type === "Polygon") {
      return polygonXml(geom.coordinates as number[][][]);
    }
    if (geom.type === "MultiPolygon") {
      const polys = (geom.coordinates as number[][][][])
        .map((p) => polygonXml(p))
        .join("");
      return `<MultiGeometry>${polys}</MultiGeometry>`;
    }
    // Otros tipos (Point, LineString) no aplican a parcelas.
    return "";
  };

  const placemarkXml = (
    p: PlotForMap,
    styleUrl: string,
    kind: LayerKey,
  ): string => {
    if (!p.geom_geojson) return "";
    const name = xmlEscape(p.name || `#${p.id}`);
    const descLines: string[] = [];
    if (p.variety_name) descLines.push(`Variedad: ${p.variety_name}`);
    if (p.surface != null)
      descLines.push(
        `Sup. ${kind === "declared" ? "licenciada" : "auditada"}: ${fmtNum(p.surface)} ha`,
      );
    if (p.surface_gps != null)
      descLines.push(`Sup. GPS: ${fmtNum(p.surface_gps)} ha`);

    // En canónicas pintamos la ficha completa: tipo de plantación,
    // patrón, marco, plantas, fechas, campaña, estado.
    if (kind === "canonical") {
      if (p.plot_type_name) descLines.push(`Tipo plantación: ${p.plot_type_name}`);
      if (p.rootstock_name) descLines.push(`Patrón: ${p.rootstock_name}`);
      if (p.plantation_frame) descLines.push(`Marco: ${p.plantation_frame}`);
      if (p.plants != null)
        descLines.push(`Plantas: ${p.plants.toLocaleString("es-ES")}`);
      if (p.crop_date) descLines.push(`Año plantación: ${p.crop_date}`);
      if (p.plantation_date)
        descLines.push(`Fecha plantación: ${p.plantation_date}`);
      if (p.season_name) descLines.push(`Campaña asignada: ${p.season_name}`);
      const stateLabel =
        p.is_active === false || p.status === "closed"
          ? `Cerrada${p.closed_date ? ` (${p.closed_date})` : ""}`
          : "Abierta";
      descLines.push(`Estado: ${stateLabel}`);
    }

    const description = xmlEscape(descLines.join("\n"));
    const geomXml = geometryToKml(p.geom_geojson);
    if (!geomXml) return "";
    return `<Placemark><name>${name}</name><description>${description}</description><styleUrl>#${styleUrl}</styleUrl>${geomXml}</Placemark>`;
  };

  const buildKml = (docBaseName: string): string => {
    const docName = xmlEscape(docBaseName);
    const docDescParts: string[] = [];
    if (farmName) docDescParts.push(`Finca: ${farmName}`);
    if (producerName) docDescParts.push(`Productor: ${producerName}`);
    if (locationLine) docDescParts.push(locationLine);
    docDescParts.push(
      `${declaredWithGeom.length} parcelas declaradas · ${canonicalWithGeom.length} parcelas canónicas`,
    );
    const docDesc = xmlEscape(docDescParts.join("\n"));

    const styleDeclared = `<Style id="declared"><LineStyle><color>${KML_STYLE_DECLARED.line}</color><width>2</width></LineStyle><PolyStyle><color>${KML_STYLE_DECLARED.fill}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>`;
    const styleCanonical = `<Style id="canonical"><LineStyle><color>${KML_STYLE_CANONICAL.line}</color><width>2</width></LineStyle><PolyStyle><color>${KML_STYLE_CANONICAL.fill}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>`;

    const declaredPlacemarks = declaredWithGeom
      .map((p) => placemarkXml(p, "declared", "declared"))
      .filter(Boolean)
      .join("");
    const canonicalPlacemarks = canonicalWithGeom
      .map((p) => placemarkXml(p, "canonical", "canonical"))
      .filter(Boolean)
      .join("");

    const declaredFolder = `<Folder><name>Parcelas declaradas (contrato)</name><description>${declaredWithGeom.length} parcelas</description>${declaredPlacemarks}</Folder>`;
    const canonicalFolder = `<Folder><name>Parcelas canónicas (auditadas)</name><description>${canonicalWithGeom.length} parcelas</description>${canonicalPlacemarks}</Folder>`;

    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${docName}</name>
    <description>${docDesc}</description>
    ${styleDeclared}
    ${styleCanonical}
    ${declaredFolder}
    ${canonicalFolder}
  </Document>
</kml>`;
  };

  const downloadKml = () => {
    try {
      // Slug del productor (alfanumérico, "_" como separador, sin
      // acentos) + sufijo aleatorio de 6 dígitos. Tanto el nombre del
      // archivo como el <name> del <Document> usan el mismo string
      // para que el destinatario los pueda relacionar.
      const producerSlug = (producerName || "finca")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60) || "finca";
      const random = Math.floor(100000 + Math.random() * 900000);
      const baseName = `${producerSlug}_${random}`;
      const kml = buildKml(baseName);
      const blob = new Blob([kml], {
        type: "application/vnd.google-earth.kml+xml",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.kml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success(`KML descargado: ${baseName}.kml`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[FarmMapCard] KML export failed", e);
      message.error("No se pudo generar el KML");
    }
  };

  /* ---- Export PNG ----------------------------------------------- */

  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  const exportMap = async () => {
    if (!mapInstance.current || !mapRef.current) return;
    const map = mapInstance.current;
    const container = mapRef.current;

    setExporting(true);
    try {
      // Asegura que las dos capas están visibles en la imagen final,
      // independientemente del estado de la leyenda.
      const wasDeclaredOn = visible.declared;
      const wasCanonicalOn = visible.canonical;
      const wasHighlighted = highlightedRef.current;
      if (highlightedRef.current) clearHighlight();
      if (!wasDeclaredOn && declaredLayer.current) {
        declaredLayer.current.addTo(map);
      }
      if (!wasCanonicalOn && canonicalLayer.current) {
        canonicalLayer.current.addTo(map);
      }

      // Encuadre apretado a TODAS las geometrías para que el snapshot
      // capture la finca completa.
      const previousCenter = map.getCenter();
      const previousZoom = map.getZoom();
      const allLayers: L.Layer[] = [];
      declaredLayer.current?.eachLayer((l) => allLayers.push(l));
      canonicalLayer.current?.eachLayer((l) => allLayers.push(l));
      let viewChanged = false;
      if (allLayers.length > 0) {
        try {
          const fg = L.featureGroup(allLayers);
          const b = fg.getBounds();
          if (b.isValid()) {
            map.fitBounds(b, {
              animate: false,
              padding: [24, 24],
              maxZoom: 19,
            });
            map.invalidateSize();
            viewChanged = true;
          }
        } catch {
          /* best-effort */
        }
      }

      // Espera a que los tiles satelitales lleguen.
      await new Promise((r) => setTimeout(r, viewChanged ? 1000 : 500));

      const containerW = container.offsetWidth;
      const containerH = container.offsetHeight;
      const pixelRatio = 2;

      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(container, {
        cacheBust: true,
        pixelRatio,
        filter: (node: HTMLElement) => {
          const cls = node?.className?.toString?.() ?? "";
          return (
            !cls.includes("leaflet-control-zoom") &&
            !cls.includes("leaflet-control-attribution") &&
            !cls.includes("leaflet-control-layers")
          );
        },
      });

      const loadImage = (src: string): Promise<HTMLImageElement | null> =>
        new Promise((resolve) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = () => resolve(null);
          im.src = src;
        });

      const [mapImg, logoImg] = await Promise.all([
        loadImage(dataUrl),
        loadImage("/logo_cyberagropolis.png"),
      ]);
      if (!mapImg) throw new Error("No se pudo decodificar el mapa.");

      // Etiqueta el polígono con el nombre de la parcela.
      const mapCanvas = document.createElement("canvas");
      mapCanvas.width = mapImg.naturalWidth;
      mapCanvas.height = mapImg.naturalHeight;
      const mapCtx = mapCanvas.getContext("2d");
      if (!mapCtx) throw new Error("No 2D ctx.");
      mapCtx.drawImage(mapImg, 0, 0);

      const scaleX = mapCanvas.width / containerW;
      const scaleY = mapCanvas.height / containerH;
      mapCtx.font = `600 ${13 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      mapCtx.textBaseline = "middle";
      mapCtx.textAlign = "center";

      type Label = { name: string; latlng: L.LatLng };
      const labels: Label[] = [];
      polysRef.current.forEach((entry, key) => {
        try {
          const c = entry.layer.getBounds().getCenter();
          const idStr = key.split(":")[1];
          // Recupera el nombre del plot original
          const matchD = declaredPlots.find(
            (p) => String(p.id) === idStr && entry.kind === "declared",
          );
          const matchC = canonicalPlots.find(
            (p) => String(p.id) === idStr && entry.kind === "canonical",
          );
          const p = matchD || matchC;
          if (p && p.name) labels.push({ name: p.name, latlng: c });
        } catch {
          /* skip */
        }
      });

      for (const { name, latlng } of labels) {
        const pt = map.latLngToContainerPoint(latlng);
        const cx = pt.x * scaleX;
        const cy = pt.y * scaleY;
        const padX = 8 * pixelRatio;
        const padY = 4 * pixelRatio;
        const radius = 6 * pixelRatio;
        const textW = mapCtx.measureText(name).width;
        const boxW = textW + padX * 2;
        const boxH = 16 * pixelRatio + padY * 2;
        const boxX = cx - boxW / 2;
        const boxY = cy - boxH / 2;
        mapCtx.fillStyle = "rgba(255, 255, 255, 0.85)";
        drawRoundedRect(mapCtx, boxX, boxY, boxW, boxH, radius);
        mapCtx.fill();
        mapCtx.lineWidth = 1 * pixelRatio;
        mapCtx.strokeStyle = "rgba(0, 0, 0, 0.35)";
        drawRoundedRect(mapCtx, boxX, boxY, boxW, boxH, radius);
        mapCtx.stroke();
        mapCtx.fillStyle = "#1f1f1f";
        mapCtx.fillText(name, cx, cy);
      }

      // Compone el PNG final con cabecera + mapa enmarcado + leyenda
      // + pie.
      const headerH = 110 * pixelRatio;
      const legendH = 56 * pixelRatio;
      const footerH = 40 * pixelRatio;
      const sidePad = 28 * pixelRatio;
      const framePad = 6 * pixelRatio;
      const accent = "#FA8C16";
      const ink = "#1f1f1f";
      const muted = "#8c8c8c";

      const finalW = mapCanvas.width + sidePad * 2;
      const finalH =
        headerH + mapCanvas.height + legendH + footerH + framePad * 2;

      const out = document.createElement("canvas");
      out.width = finalW;
      out.height = finalH;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("No 2D ctx final.");

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalW, finalH);

      // Cabecera
      const headerInnerY = 20 * pixelRatio;
      const logoTargetH = 38 * pixelRatio;
      let textX = sidePad;
      if (logoImg) {
        const ratio = logoImg.naturalHeight
          ? logoImg.naturalWidth / logoImg.naturalHeight
          : 1;
        const logoW = logoTargetH * ratio;
        ctx.drawImage(logoImg, sidePad, headerInnerY, logoW, logoTargetH);
        textX = sidePad + logoW + 20 * pixelRatio;
      }
      ctx.textBaseline = "top";
      ctx.textAlign = "left";
      ctx.fillStyle = ink;
      ctx.font = `700 ${22 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.fillText(`Finca${farmName ? ` · ${farmName}` : ""}`, textX, headerInnerY);

      ctx.fillStyle = muted;
      ctx.font = `500 ${14 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      const subParts: string[] = [];
      if (producerName) subParts.push(`Productor: ${producerName}`);
      if (locationLine) subParts.push(locationLine);
      ctx.fillText(subParts.join("   ·   "), textX, headerInnerY + 32 * pixelRatio);
      ctx.font = `400 ${12 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.fillText(
        "Mapa de parcelas — declaradas (contrato) y canónicas (auditadas)",
        textX,
        headerInnerY + 56 * pixelRatio,
      );

      // Línea de acento
      ctx.fillStyle = accent;
      ctx.fillRect(
        sidePad,
        headerH - 4 * pixelRatio,
        finalW - sidePad * 2,
        2 * pixelRatio,
      );

      // Mapa enmarcado
      const frameX = sidePad - framePad;
      const frameY = headerH;
      const frameW = mapCanvas.width + framePad * 2;
      const frameH = mapCanvas.height + framePad * 2;
      ctx.lineWidth = 1 * pixelRatio;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.18)";
      drawRoundedRect(ctx, frameX, frameY, frameW, frameH, 10 * pixelRatio);
      ctx.stroke();
      ctx.save();
      ctx.shadowColor = "rgba(0, 0, 0, 0.10)";
      ctx.shadowBlur = 12 * pixelRatio;
      ctx.shadowOffsetY = 3 * pixelRatio;
      ctx.drawImage(
        mapCanvas,
        sidePad,
        headerH + framePad,
        mapCanvas.width,
        mapCanvas.height,
      );
      ctx.restore();

      // Leyenda
      const legendY = headerH + mapCanvas.height + framePad * 2 + 8 * pixelRatio;
      ctx.textBaseline = "middle";
      ctx.fillStyle = ink;
      ctx.font = `600 ${13 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.fillText("LEYENDA", sidePad, legendY + 14 * pixelRatio);

      const drawLegendItem = (
        x: number,
        y: number,
        color: string,
        dashed: boolean,
        label: string,
        count: number,
      ) => {
        const swatchSize = 18 * pixelRatio;
        // swatch background
        ctx.fillStyle = color + "33"; // 20% alpha hex
        ctx.fillRect(x, y - swatchSize / 2, swatchSize, swatchSize);
        ctx.lineWidth = 2 * pixelRatio;
        ctx.strokeStyle = color;
        if (dashed) ctx.setLineDash([4 * pixelRatio, 3 * pixelRatio]);
        ctx.strokeRect(x, y - swatchSize / 2, swatchSize, swatchSize);
        ctx.setLineDash([]);
        ctx.fillStyle = ink;
        ctx.font = `500 ${12 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
        ctx.textAlign = "left";
        ctx.fillText(`${label} (${count})`, x + swatchSize + 8 * pixelRatio, y);
      };

      const legendItemY = legendY + 38 * pixelRatio;
      drawLegendItem(
        sidePad,
        legendItemY,
        COLOR_DECLARED,
        true,
        "Declaradas (contrato)",
        declaredWithGeom.length,
      );
      drawLegendItem(
        sidePad + 250 * pixelRatio,
        legendItemY,
        COLOR_CANONICAL,
        false,
        "Canónicas (auditadas)",
        canonicalWithGeom.length,
      );

      // Pie
      const footerTextY =
        headerH + mapCanvas.height + framePad * 2 + legendH + 14 * pixelRatio;
      ctx.fillStyle = muted;
      ctx.font = `500 ${12 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("Generado por Cyberagropolis", sidePad, footerTextY);
      const today = new Date().toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      ctx.textAlign = "right";
      ctx.fillText(today, finalW - sidePad, footerTextY);

      // Descarga
      const blob: Blob = await new Promise<Blob>((resolve, reject) => {
        out.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error("toBlob devolvió null"));
        }, "image/png");
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mapa-finca-${(farmName || "finca").replace(/[^\w-]+/g, "_")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success("Mapa descargado");

      // Restaura el estado anterior del usuario.
      if (!wasDeclaredOn && declaredLayer.current) {
        map.removeLayer(declaredLayer.current);
      }
      if (!wasCanonicalOn && canonicalLayer.current) {
        map.removeLayer(canonicalLayer.current);
      }
      if (viewChanged) {
        try {
          map.setView(previousCenter, previousZoom, { animate: false });
        } catch {
          /* best-effort */
        }
      }
      if (wasHighlighted) {
        const entry = polysRef.current.get(wasHighlighted);
        if (entry) {
          entry.layer.setStyle(highlightStyle(entry.kind));
          highlightedRef.current = wasHighlighted;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[FarmMapCard] export failed", e);
      message.error("No se pudo descargar el mapa");
    } finally {
      setExporting(false);
    }
  };

  const legendBar = hasAnyGeom && (
    <div
      style={{
        padding: "10px 12px",
        background: "#fafafa",
        borderBottom: "1px solid #f0f0f0",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}
    >
      <Typography.Text type="secondary" style={{ fontSize: 11, marginRight: 4 }}>
        CAPAS:
      </Typography.Text>
      <LegendChip
        label="Declaradas (contrato)"
        kind="declared"
        count={declaredWithGeom.length}
        on={visible.declared}
        onClick={() => setVisible((v) => ({ ...v, declared: !v.declared }))}
      />
      <LegendChip
        label="Canónicas"
        kind="canonical"
        count={canonicalWithGeom.length}
        on={visible.canonical}
        onClick={() => setVisible((v) => ({ ...v, canonical: !v.canonical }))}
      />
    </div>
  );

  return (
    <Card
      size="small"
      title={
        <Space>
          <EnvironmentOutlined />
          <strong>Mapa de parcelas</strong>
        </Space>
      }
      extra={
        hasAnyGeom && (
          <Space size={4}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={downloadKml}
            >
              KML
            </Button>
            <Button
              size="small"
              icon={<CameraOutlined />}
              onClick={exportMap}
              loading={exporting}
            >
              Descargar mapa
            </Button>
          </Space>
        )
      }
      bordered={false}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: hasAnyGeom ? 0 : 16 }}
    >
      {hasAnyGeom ? (
        <>
          {legendBar}
          <div
            ref={mapRef}
            style={{ height, width: "100%", borderRadius: 0 }}
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Ninguna parcela de esta finca tiene geometría dibujada"
        />
      )}
    </Card>
  );
};

export default FarmMapCard;
